import { useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export const LIST_PARAMS = {
  page: "page",
  keyword: "q",
  status: "status",
  sortBy: "sort",
  sortDir: "dir",
} as const;

type ParamsMutator = (params: URLSearchParams) => void;

export function useSearchParamsWriter(): [URLSearchParams, (mutate: ParamsMutator, options?: { push?: boolean }) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const setRef = useRef(setSearchParams);
  setRef.current = setSearchParams;

  const write = useCallback((mutate: ParamsMutator, options?: { push?: boolean }) => {
    const current = new URLSearchParams(window.location.search);
    const next = new URLSearchParams(current);
    mutate(next);
    if (next.toString() === current.toString()) return;
    setRef.current(next, { replace: !options?.push });
  }, []);

  return [searchParams, write];
}

export function useUrlParam<T extends string>(
  key: string,
  fallback: NoInfer<T>,
  allowed?: readonly NoInfer<T>[],
  options?: { push?: boolean },
): [T, (value: T) => void] {
  const [params, write] = useSearchParamsWriter();
  const raw = params.get(key);
  const value = raw !== null && (!allowed || allowed.includes(raw as T)) ? (raw as T) : fallback;

  const setValue = useCallback(
    (next: T) =>
      write((p) => {
        if (next === fallback) p.delete(key);
        else p.set(key, next);
        if (key !== LIST_PARAMS.page) p.delete(LIST_PARAMS.page);
      }, options),
    [write, key, fallback, options],
  );

  return [value, setValue];
}
